import { useEffect, useState } from 'react';

import { ObjectToolbarView } from './ObjectToolbarView';
import { ToolbarSettingsDialog } from './parts/ToolbarSettingsDialog';
import { type ObjectToolbarConfig, useObjectToolbar } from './useObjectToolbar';
import { useObjectToolbarFormStore } from './useObjectToolbarFormStore';
import {
  DEFAULT_LABEL_REVEAL_MODE,
  useObjectToolbarSettingsStore,
} from './useObjectToolbarSettingsStore';

export interface ObjectToolbarProps {
  /** Declarative toolbar config — resolved through {@link useObjectToolbar}. */
  config: ObjectToolbarConfig;
}

/**
 * Object-level toolbar, connected. Resolves the declarative `config` through
 * {@link useObjectToolbar} (permissions, form state, save/cancel wiring) and
 * renders the presentational {@link ObjectToolbarView}. This is the component
 * pages mount; the view stays a pure, prop-driven presentational layer beneath.
 */
export const ObjectToolbar = ({ config }: ObjectToolbarProps) => {
  // `loading` from useObjectToolbar is intentionally not forwarded: actions
  // already fail closed while permissions resolve, and the presentational block
  // has no skeleton/disabled API for it yet (reserved for Phase 1). Don't wire
  // it in until that API exists — see useObjectToolbar's JSDoc.
  const { props } = useObjectToolbar(config);

  // User display preferences (persisted, set from the Toolbar settings modal).
  // The preference wins; the fixed default only applies until the user makes an
  // explicit choice (see DEFAULT_LABEL_REVEAL_MODE — this replaced the former
  // object_toolbar_button_title_slideout flag).
  const showActionLabels = useObjectToolbarSettingsStore(
    (s) => s.showActionLabels
  );
  const labelRevealMode = useObjectToolbarSettingsStore(
    (s) => s.labelRevealMode
  );
  const defaultRevealMode = DEFAULT_LABEL_REVEAL_MODE;
  const primaryTitleSlideout =
    (labelRevealMode ?? defaultRevealMode) === 'slideout';

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Signal toolbar presence so relocating publishers (FormMenu, ribbon) only move
  // their controls here when a toolbar is actually on the page — see the store's
  // `toolbarMounted` JSDoc.
  const setToolbarMounted = useObjectToolbarFormStore(
    (s) => s.setToolbarMounted
  );
  useEffect(() => {
    setToolbarMounted(true);

    return () => setToolbarMounted(false);
  }, [setToolbarMounted]);

  return (
    <>
      <ObjectToolbarView
        {...props}
        primaryTitleSlideout={primaryTitleSlideout}
        showActionLabels={showActionLabels}
        onOpenSettings={() => setSettingsOpen(true)}
      />
      <ToolbarSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        defaultRevealMode={defaultRevealMode}
      />
    </>
  );
};
